import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, BookOpen } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import SmartImage from "../components/SmartImage";
import { api } from "../lib/config";
import useSEO from "../hooks/useSEO";

const RealisationsPage = () => {
  useSEO({
    title: "Nos Réalisations",
    description: "Galerie de réalisations : verres gravés, mugs personnalisés, coffrets cadeaux, vases, couteaux, planches à découper, plaques métal, décoration murale, créations sur mesure.",
  });
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      const [g, c] = await Promise.all([api.get("/gallery"), api.get("/gallery-categories")]);
      setItems(g.data);
      setCats(c.data);
    })();
  }, []);

  const allCats = [{ slug: "all", label: "Toutes" }, ...cats];
  const filtered = active === "all" ? items : items.filter(i => i.category === active);
  const labelOf = (slug) => cats.find(c => c.slug === slug)?.label || slug;

  return (
    <div data-testid="realisations-page">
      <section className="pt-32 pb-16 bg-[#171717]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Ornament />
          <SectionOverline className="mt-6">Nos réalisations</SectionOverline>
          <h1 className="font-serif text-5xl sm:text-6xl text-white mt-4 mb-6">Un portfolio sur mesure</h1>
          <p className="text-base sm:text-lg text-[#DDD4B2]/80 max-w-2xl mx-auto">Découvrez nos créations classées par catégories. Chaque pièce est réalisée en atelier, selon les souhaits de nos clients.</p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="py-10 bg-[#171717] sticky top-0 z-30 border-y border-[#9A8640]/20 backdrop-blur-xl bg-[#171717]/85">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-3 justify-center" data-testid="category-filters">
          {allCats.map((c) => (
            <button
              key={c.slug}
              onClick={() => setActive(c.slug)}
              className={`px-5 py-2 text-xs uppercase tracking-[0.18em] rounded-full border transition-all ${
                active === c.slug ? "bg-[#9A8640] text-[#171717] border-[#9A8640]" : "text-[#DDD4B2]/80 border-[#9A8640]/40 hover:border-[#9A8640] hover:text-[#9A8640]"
              }`}
              data-testid={`filter-${c.slug}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 bg-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              {filtered.map((g, i) => (
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: (i % 8) * 0.05 }}
                  className="group relative aspect-square overflow-hidden cursor-pointer"
                  onClick={() => setSelected(g)}
                  data-testid={`gallery-item-${g.id}`}
                >
                  <SmartImage src={g.image_url} alt={`${g.title} — ${labelOf(g.category)} par Passion Verre`} width={600} eager={i < 8} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#171717] via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity" />
                  {g.blog_slug && (
                    <span className="absolute top-3 right-3 bg-[#9A8640] text-[#171717] text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full inline-flex items-center gap-1">
                      <BookOpen size={10} /> Article
                    </span>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-5 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#9A8640]">{labelOf(g.category)}</p>
                    <h3 className="font-serif text-lg text-white mt-1">{g.title}</h3>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
          {filtered.length === 0 && (
            <p className="text-center text-[#DDD4B2]/60 py-20">Aucune réalisation dans cette catégorie pour le moment.</p>
          )}
        </div>
      </section>

      {/* AKM LASER */}
      <section className="py-24 bg-[#DDD4B2] text-[#171717]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionOverline>Partenaire</SectionOverline>
          <h2 className="font-serif text-3xl sm:text-4xl mt-3 mb-6">Envie de vous équiper ?</h2>
          <p className="text-base leading-relaxed mb-8 text-[#171717]/85">
            Toutes les gravures et découpes que vous voyez ici sont réalisées avec les machines AKM Laser. Fiables, performantes et faciles à prendre en main, elles permettent de personnaliser, découper et graver une infinité de supports (bois, métal, cuir, verre, acrylique…). Et la bonne nouvelle : vous pouvez vous aussi vous équiper et lancer vos propres créations !
          </p>
          <a href="https://akmlaser.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#171717] text-[#DDD4B2] px-8 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#9A8640] hover:text-[#171717] transition-all" data-testid="akm-link">
            Découvrir AKM Laser <ExternalLink size={14} />
          </a>
        </div>
      </section>

      {/* LIGHTBOX */}
      {selected && (
        <div className="fixed inset-0 z-[60] bg-[#171717]/95 flex items-center justify-center p-6" onClick={() => setSelected(null)} data-testid="gallery-lightbox">
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={selected.image_url} alt={selected.title} loading="eager" decoding="async" className="w-full max-h-[75vh] object-contain" />
            <div className="mt-6 text-center">
              <p className="text-xs uppercase tracking-[0.18em] text-[#9A8640]">{labelOf(selected.category)}</p>
              <h3 className="font-serif text-3xl text-white mt-1">{selected.title}</h3>
              {selected.description && <p className="text-[#DDD4B2]/80 mt-3 max-w-2xl mx-auto">{selected.description}</p>}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                {selected.slug && (
                  <Link
                    to={`/realisations/${selected.slug}`}
                    onClick={() => setSelected(null)}
                    className="btn-gold px-5 py-2.5 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2"
                    data-testid="lightbox-detail-link"
                  >
                    Voir la fiche complète →
                  </Link>
                )}
                {selected.blog_slug && (
                  <Link
                    to={`/blog/${selected.blog_slug}`}
                    onClick={() => setSelected(null)}
                    className="inline-flex items-center gap-2 border border-[#9A8640] text-[#9A8640] px-5 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-[#9A8640] hover:text-[#171717] transition-colors"
                    data-testid="lightbox-blog-link"
                  >
                    <BookOpen size={14} /> Lire l'article associé
                  </Link>
                )}
                <button onClick={() => setSelected(null)} className="text-xs uppercase tracking-[0.18em] text-[#9A8640] border-b border-[#9A8640]">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealisationsPage;
